import api from "@/lib/api";

const RTC_CONFIG = {
  iceServers: [
    { urls: "stun:stun.l.google.com:19302" },
    { urls: "stun:stun1.l.google.com:19302" },
  ],
};

// WebRTC voice session with signaling exchanged via backend polling.
export class SpeakEngine {
  constructor(handlers = {}) {
    this.h = handlers;
    this.pc = null;
    this.localStream = null;
    this.sessionId = null;
    this.role = null;
    this.poll = null;
    this.candQueue = [];
    this.appliedRemote = 0;
    this.remoteReady = false;
    this.ended = false;
    this.connectTimer = null;
    this.poorNotified = false;
  }

  async _setupPc() {
    this.localStream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
    this.h.onLocalStream?.(this.localStream);
    const pc = new RTCPeerConnection(RTC_CONFIG);
    this.pc = pc;
    this.localStream.getTracks().forEach((t) => pc.addTrack(t, this.localStream));
    pc.ontrack = (e) => this.h.onRemoteStream?.(e.streams[0]);
    pc.onicecandidate = (e) => {
      if (!e.candidate) return;
      const c = e.candidate.toJSON();
      if (this.sessionId) this._postCandidate(c);
      else this.candQueue.push(c);
    };
    const onConn = () => {
      const st = pc.connectionState;
      if (st === "connected") { this._clearConnectTimer(); this.h.onState?.("connected"); }
      else if (st === "failed") { this._poor(); }
    };
    const onIce = () => {
      const st = pc.iceConnectionState;
      if (st === "connected" || st === "completed") { this._clearConnectTimer(); this.h.onState?.("connected"); }
      else if (st === "failed" || st === "disconnected") { this._poor(); }
    };
    pc.onconnectionstatechange = onConn;
    pc.oniceconnectionstatechange = onIce;
    return pc;
  }

  _startConnectTimer() {
    this._clearConnectTimer();
    this.connectTimer = setTimeout(() => this._poor(), 12000);
  }
  _clearConnectTimer() { if (this.connectTimer) { clearTimeout(this.connectTimer); this.connectTimer = null; } }
  _poor() { if (!this.poorNotified) { this.poorNotified = true; this.h.onPoorNetwork?.(); } }

  async _postCandidate(c) {
    try { await api.post(`/speak/requests/${this.sessionId}/candidate`, { candidate: c }); } catch (e) { console.debug("speak: candidate post failed", e); }
  }

  async startCaller(conversationId) {
    this.role = "caller";
    const pc = await this._setupPc();
    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);
    const { data } = await api.post(`/speak/requests`, {
      conversation_id: conversationId,
      offer: { type: offer.type, sdp: offer.sdp },
    });
    this.sessionId = data.id;
    this.h.onSession?.(data.id);
    this.candQueue.forEach((c) => this._postCandidate(c));
    this.candQueue = [];
    this.h.onState?.("waiting");
    this._startPolling();
  }

  async startCallee(sessionId) {
    this.role = "callee";
    this.sessionId = sessionId;
    const { data: s } = await api.get(`/speak/requests/${sessionId}`);
    const pc = await this._setupPc();
    await pc.setRemoteDescription(new RTCSessionDescription(s.offer));
    this.remoteReady = true;
    const answer = await pc.createAnswer();
    await pc.setLocalDescription(answer);
    await api.post(`/speak/requests/${sessionId}/accept`, {
      answer: { type: answer.type, sdp: answer.sdp },
    });
    this.h.onState?.("connecting");
    this._startConnectTimer();
    this._startPolling();
  }

  _startPolling() {
    this.poll = setInterval(() => this._tick(), 1200);
    this._tick();
  }

  async _tick() {
    if (this.ended || !this.sessionId) return;
    let s;
    try { const r = await api.get(`/speak/requests/${this.sessionId}`); s = r.data; }
    catch (e) { console.debug("speak: poll failed", e); return; }
    if (s.status === "ended") { this.end(false); this.h.onEnded?.(); return; }
    if (this.role === "caller" && s.status === "active" && s.answer && !this.remoteReady) {
      try {
        await this.pc.setRemoteDescription(new RTCSessionDescription(s.answer));
        this.remoteReady = true;
        this.h.onState?.("connecting");
        this._startConnectTimer();
      } catch (e) { console.debug("speak: setRemoteDescription failed", e); }
    }
    if (this.remoteReady && this.pc) {
      const list = (this.role === "caller" ? s.callee_candidates : s.caller_candidates) || [];
      for (let i = this.appliedRemote; i < list.length; i++) {
        try { await this.pc.addIceCandidate(new RTCIceCandidate(list[i])); } catch (e) { console.debug("speak: addIceCandidate failed", e); }
      }
      this.appliedRemote = list.length;
    }
  }

  async end(notify = true) {
    if (this.ended) return;
    this.ended = true;
    if (this.poll) clearInterval(this.poll);
    this._clearConnectTimer();
    if (notify && this.sessionId) {
      try { await api.post(`/speak/requests/${this.sessionId}/end`); } catch (e) { console.debug("speak: end notify failed", e); }
    }
    try { this.localStream?.getTracks().forEach((t) => t.stop()); } catch (e) { console.debug("speak: stop tracks failed", e); }
    try { this.pc?.close(); } catch (e) { console.debug("speak: pc close failed", e); }
  }
}
